import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Card, Button, Space, Typography, Spin, Result, Tag, Alert, Modal, Input, message, Badge } from 'antd'
import {
  ArrowLeftOutlined,
  VideoCameraOutlined,
  PhoneOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  ExclamationCircleOutlined,
  MessageOutlined,
  SendOutlined,
  CloseOutlined,
} from '@ant-design/icons'
import Daily, { DailyCall } from '@daily-co/daily-js'

const { Title, Text, Paragraph } = Typography
const { TextArea } = Input

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('admin_access_token')
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  }
}

// API helper functions
async function completeAppointmentApi(appointmentId: string, notes?: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_URL}/admin/consultations/my/${appointmentId}/complete`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ notes }),
    })
    return response.ok
  } catch (err) {
    console.error('Failed to complete appointment:', err)
    return false
  }
}

async function markNoShowApi(appointmentId: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_URL}/admin/consultations/my/${appointmentId}/no-show`, {
      method: 'POST',
      headers: getAuthHeaders(),
    })
    return response.ok
  } catch (err) {
    console.error('Failed to mark no-show:', err)
    return false
  }
}

async function markNeedsRescheduleApi(appointmentId: string, reason?: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_URL}/admin/consultations/my/${appointmentId}/needs-reschedule`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ reason }),
    })
    return response.ok
  } catch (err) {
    console.error('Failed to mark needs reschedule:', err)
    return false
  }
}

async function markDoctorJoinedApi(appointmentId: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_URL}/admin/consultations/my/${appointmentId}/joined`, {
      method: 'POST',
      headers: getAuthHeaders(),
    })
    return response.ok
  } catch (err) {
    console.error('Failed to mark doctor joined:', err)
    return false
  }
}

interface AppointmentDetail {
  id: string
  patient: {
    id: string
    name: string
    email: string
    phone: string | null
  }
  typeLabel: string
  scheduledAt: string
  duration: number
  status: string
  roomUrl: string | null
}

interface ChatMessage {
  id: string
  sender: string
  text: string
  timestamp: Date
  isLocal: boolean
}

type CallState = 'pre-call' | 'in-call' | 'post-call'

export default function DoctorCallPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const callFrameRef = useRef<DailyCall | null>(null)
  const videoContainerRef = useRef<HTMLDivElement>(null)

  const [data, setData] = useState<AppointmentDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [joinToken, setJoinToken] = useState<string | null>(null)
  const [roomUrl, setRoomUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [callState, setCallState] = useState<CallState>('pre-call')

  // Chat state
  const [showChat, setShowChat] = useState(false)
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([])
  const [chatInput, setChatInput] = useState('')
  const [unreadCount, setUnreadCount] = useState(0)
  const chatMessagesEndRef = useRef<HTMLDivElement>(null)

  // Post-call state
  const [showRescheduleModal, setShowRescheduleModal] = useState(false)
  const [rescheduleReason, setRescheduleReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [doctorNotes, setDoctorNotes] = useState('')

  // Scroll to bottom of chat when new messages arrive
  useEffect(() => {
    chatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatMessages])

  // Reset unread count when chat is opened
  useEffect(() => {
    if (showChat) {
      setUnreadCount(0)
    }
  }, [showChat])

  // Fetch appointment data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/admin/consultations/my/${id}`, {
          headers: getAuthHeaders(),
        })
        if (!res.ok) throw new Error('Failed to fetch')
        const json = await res.json()
        setData(json)
      } catch (err) {
        console.error('Error fetching appointment:', err)
        setError('Greska pri ucitavanju konsultacije')
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [id])

  // Get join token when consultation data is loaded. The backend's
  // ensureDailyRoom() JIT-creates/verifies the Daily room, so we call the
  // token endpoint whenever we have a consultation id — do NOT gate on the
  // (potentially stale or null) data.roomUrl here.
  useEffect(() => {
    const getJoinToken = async () => {
      if (!data || !id) return

      try {
        const res = await fetch(
          `${API_URL}/admin/consultations/my/${id}/join-token`,
          { headers: getAuthHeaders() }
        )

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}))
          throw new Error(errorData.message || 'Nije moguce dobiti pristup')
        }

        const json = await res.json()

        setJoinToken(json.token)
        setRoomUrl(json.roomUrl)
      } catch (err) {
        console.error('Error getting join token:', err)
        setError(err instanceof Error ? err.message : 'Greska pri dobijanju pristupa video pozivu')
      }
    }

    getJoinToken()
  }, [data, id])

  // Cleanup Daily call frame on unmount
  useEffect(() => {
    return () => {
      if (callFrameRef.current) {
        callFrameRef.current.destroy()
        callFrameRef.current = null
      }
    }
  }, [])

  // Join call when entering in-call state (handles initial join and rejoin)
  useEffect(() => {
    if (callState === 'in-call' && roomUrl && joinToken && !callFrameRef.current) {
      // Wait for next tick to ensure videoContainerRef is mounted
      const timer = setTimeout(() => {
        if (videoContainerRef.current) {
          joinCall()
        }
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [callState, roomUrl, joinToken])

  const joinCall = async () => {
    if (!roomUrl || !joinToken || !id || !videoContainerRef.current || callFrameRef.current) return

    // Create Daily call frame with built-in chat disabled (we use our own)
    const callFrame = Daily.createFrame(videoContainerRef.current, {
      iframeStyle: {
        width: '100%',
        height: '100%',
        border: 'none',
      },
      showLeaveButton: false,
      showFullscreenButton: true,
      showChat: false,  // Disable Daily's built-in chat - we use our custom chat panel
    })

    // Store reference
    callFrameRef.current = callFrame

    // Set up event listeners
    callFrame.on('app-message', handleAppMessage)

    callFrame.on('left-meeting', () => {
      console.log('[DoctorCall] Left meeting')
    })

    callFrame.on('error', (e) => {
      console.error('[DoctorCall] Error:', e)
    })

    // Join the call
    try {
      await callFrame.join({
        url: roomUrl,
        token: joinToken,
      })
      console.log('[DoctorCall] Joined meeting')
    } catch (err) {
      console.error('[DoctorCall] Failed to join:', err)
      setError('Greška pri pridruživanju pozivu')
    }
  }

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleTimeString('sr-Latn-RS', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatChatTime = (date: Date) => {
    return date.toLocaleTimeString('sr-Latn-RS', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Use ref to track showChat state for the callback (avoids stale closure)
  const showChatRef = useRef(showChat)
  useEffect(() => {
    showChatRef.current = showChat
  }, [showChat])

  // Handle incoming app messages (chat) - stable callback using refs
  const handleAppMessage = useCallback((event: { fromId: string; data?: unknown }) => {
    const msgData = event.data as { type?: string; sender?: string; text?: string; timestamp?: string } | undefined

    if (msgData?.type === 'chat' && msgData.text) {
      const newMessage: ChatMessage = {
        id: `${event.fromId}-${Date.now()}`,
        sender: msgData.sender || 'Pacijent',
        text: msgData.text,
        timestamp: msgData.timestamp ? new Date(msgData.timestamp) : new Date(),
        isLocal: false,
      }

      setChatMessages((prev) => [...prev, newMessage])

      // Increment unread count if chat is closed (using ref for current value)
      if (!showChatRef.current) {
        setUnreadCount((prev) => prev + 1)
      }
    }
  }, []) // Empty dependency array - callback is stable

  const handleJoinCall = useCallback(async () => {
    if (!roomUrl || !joinToken || !id) return

    // Mark doctor as joined
    await markDoctorJoinedApi(id)

    // Transition to in-call state - the useEffect will handle actual joining
    setCallState('in-call')
  }, [roomUrl, joinToken, id])

  const handleLeaveCall = useCallback(async () => {
    // Leave the Daily call. `destroy()` is async under the hood — it tears
    // down iframe DOM + postMessage channels. If we immediately flip to
    // post-call state, React unmounts the container and Daily's internal
    // event pipe races into a "postMessage on null" error when the next
    // rejoin tries to createFrame. Await destroy() and null the ref
    // BEFORE the state flip so the next joinCall starts from a clean
    // slate.
    if (callFrameRef.current) {
      try {
        await callFrameRef.current.leave()
      } catch {
        // leave() throws if not yet joined — ignore.
      }
      try {
        await callFrameRef.current.destroy()
      } catch {
        // destroy() can also throw during teardown race — ignore.
      }
      callFrameRef.current = null
    }

    // Show post-call decision screen
    setCallState('post-call')
  }, [])

  const handleSendChatMessage = useCallback(() => {
    const text = chatInput.trim()
    if (!text || !callFrameRef.current) return

    const timestamp = new Date()
    const messageData = {
      type: 'chat',
      sender: 'Dr. ' + (data?.patient?.name ? 'Lekar' : 'Lekar'),
      text,
      timestamp: timestamp.toISOString(),
    }

    // Send via Daily
    try {
      callFrameRef.current.sendAppMessage(messageData, '*')
    } catch (err) {
      console.error('[DoctorCall] Failed to send message:', err)
    }

    // Add to local messages
    const localMessage: ChatMessage = {
      id: `local-${Date.now()}`,
      sender: 'Vi',
      text,
      timestamp,
      isLocal: true,
    }

    setChatMessages((prev) => [...prev, localMessage])
    setChatInput('')
  }, [chatInput, data])

  const handleCompleteConsultation = async () => {
    if (!id) return
    setIsSubmitting(true)

    const success = await completeAppointmentApi(id, doctorNotes || undefined)

    if (success) {
      message.success('Konsultacija je uspešno završena')
      navigate(`/my-consultations/${id}`)
    } else {
      message.error('Greška pri završavanju konsultacije')
    }

    setIsSubmitting(false)
  }

  const handleMarkNoShow = async () => {
    if (!id) return
    setIsSubmitting(true)

    const success = await markNoShowApi(id)

    if (success) {
      message.success('Pacijent je označen kao "nije se pojavio"')
      navigate(`/my-consultations/${id}`)
    } else {
      message.error('Greška pri označavanju')
    }

    setIsSubmitting(false)
  }

  const handleNeedsReschedule = async () => {
    if (!id) return
    setIsSubmitting(true)

    const success = await markNeedsRescheduleApi(id, rescheduleReason || undefined)

    if (success) {
      message.success('Termin je označen za ponovno zakazivanje. Sestra će kontaktirati pacijenta.')
      navigate(`/my-consultations/${id}`)
    } else {
      message.error('Greška pri označavanju')
    }

    setIsSubmitting(false)
    setShowRescheduleModal(false)
  }

  const handleRejoinCall = async () => {
    // Refetch a fresh join token — the previous one may be close to
    // expiry, and if we reuse a stale token the new Daily frame rejects
    // with a silent error (no visible feedback in the UI).
    if (id) {
      try {
        const res = await fetch(
          `${API_URL}/admin/consultations/my/${id}/join-token`,
          { headers: getAuthHeaders() }
        )
        if (res.ok) {
          const json = await res.json()
          setJoinToken(json.token)
          setRoomUrl(json.roomUrl)
        }
      } catch (err) {
        console.error('Error refreshing join token:', err)
      }
    }
    setCallState('in-call')
    // Re-join will happen when the in-call UI mounts
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" tip="Ucitavanje..." />
      </div>
    )
  }

  if (error || !data) {
    return (
      <Result
        status="error"
        title="Greska"
        subTitle={error || 'Nije moguce ucitati konsultaciju'}
        extra={
          <Link to="/my-consultations">
            <Button type="primary">Nazad na listu</Button>
          </Link>
        }
      />
    )
  }

  // roomUrl here is the authoritative one returned by the join-token endpoint.
  // If we are already in pre-call/in-call, the token request is still in flight
  // or has succeeded; we only bail out of rendering if it actually failed
  // (handled above via `error`). Do NOT check data.roomUrl — it may be null for
  // legacy bookings that will be JIT-created on the backend.

  // Post-call decision screen
  if (callState === 'post-call') {
    return (
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '40px 20px' }}>
        <Card>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <ExclamationCircleOutlined style={{ fontSize: 40, color: '#1e3a5f' }} />
            </div>

            <Title level={3} style={{ marginBottom: 8 }}>Poziv je završen</Title>
            <Paragraph type="secondary">
              Molimo izaberite ishod konsultacije sa pacijentom {data.patient.name}
            </Paragraph>
          </div>

          {/* Notes input */}
          <div style={{ marginBottom: 24 }}>
            <Text strong>Beleške lekara (opcionalno):</Text>
            <TextArea
              value={doctorNotes}
              onChange={(e) => setDoctorNotes(e.target.value)}
              placeholder="Unesite beleške sa konsultacije..."
              rows={3}
              style={{ marginTop: 8 }}
            />
          </div>

          {/* Action buttons */}
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {/* Complete consultation */}
            <Button
              type="primary"
              size="large"
              block
              icon={<CheckCircleOutlined />}
              onClick={handleCompleteConsultation}
              loading={isSubmitting}
              style={{
                height: 56,
                background: '#52c41a',
                borderColor: '#52c41a',
              }}
            >
              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginLeft: 8 }}>
                <span style={{ fontWeight: 600 }}>Završi konsultaciju</span>
                <span style={{ fontSize: 12, opacity: 0.9 }}>Konsultacija je uspešno obavljena</span>
              </span>
            </Button>

            {/* Mark as no-show */}
            <Button
              size="large"
              block
              icon={<CloseCircleOutlined />}
              onClick={handleMarkNoShow}
              loading={isSubmitting}
              style={{
                height: 56,
                borderColor: '#ff4d4f',
                color: '#ff4d4f',
              }}
            >
              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginLeft: 8 }}>
                <span style={{ fontWeight: 600 }}>Pacijent se nije pojavio</span>
                <span style={{ fontSize: 12, opacity: 0.7 }}>Pacijent nije pristupio pozivu</span>
              </span>
            </Button>

            {/* Request reschedule */}
            <Button
              size="large"
              block
              icon={<CalendarOutlined />}
              onClick={() => setShowRescheduleModal(true)}
              loading={isSubmitting}
              style={{
                height: 56,
                borderColor: '#faad14',
                color: '#faad14',
              }}
            >
              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginLeft: 8 }}>
                <span style={{ fontWeight: 600 }}>Potrebno ponovo zakazati</span>
                <span style={{ fontSize: 12, opacity: 0.7 }}>Sestra će kontaktirati pacijenta</span>
              </span>
            </Button>

            <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16, marginTop: 8 }}>
              {/* Rejoin call */}
              <Button
                type="default"
                size="large"
                block
                icon={<VideoCameraOutlined />}
                onClick={handleRejoinCall}
                style={{ height: 48 }}
              >
                Vrati se u poziv
              </Button>
            </div>
          </Space>
        </Card>

        {/* Reschedule reason modal */}
        <Modal
          title="Razlog za ponovno zakazivanje"
          open={showRescheduleModal}
          onOk={handleNeedsReschedule}
          onCancel={() => setShowRescheduleModal(false)}
          okText="Označi za zakazivanje"
          cancelText="Otkaži"
          confirmLoading={isSubmitting}
        >
          <Paragraph type="secondary">
            Sestra će videti ovaj zahtev i kontaktirati pacijenta radi ponovnog zakazivanja.
          </Paragraph>
          <TextArea
            value={rescheduleReason}
            onChange={(e) => setRescheduleReason(e.target.value)}
            placeholder="Opišite razlog za ponovno zakazivanje (opcionalno)..."
            rows={4}
          />
        </Modal>
      </div>
    )
  }

  // Show video call with Daily.js
  if (callState === 'in-call' && roomUrl && joinToken) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 24px',
          background: 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 100%)',
          color: 'white',
        }}>
          <Space>
            <VideoCameraOutlined style={{ fontSize: 20 }} />
            <div>
              <Text style={{ color: 'white', fontWeight: 500 }}>{data.typeLabel}</Text>
              <br />
              <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
                Pacijent: {data.patient.name}
              </Text>
            </div>
          </Space>
          <Space>
            {/* Chat toggle button */}
            <Badge count={unreadCount} size="small">
              <Button
                type={showChat ? 'primary' : 'default'}
                icon={<MessageOutlined />}
                onClick={() => setShowChat(!showChat)}
                style={{
                  background: showChat ? '#1890ff' : 'rgba(255,255,255,0.2)',
                  borderColor: 'transparent',
                  color: 'white',
                }}
              >
                Chat
              </Button>
            </Badge>
            <Button
              danger
              type="primary"
              icon={<PhoneOutlined />}
              onClick={handleLeaveCall}
            >
              Napusti poziv
            </Button>
          </Space>
        </div>

        {/* Main content area */}
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Video container */}
          <div
            ref={videoContainerRef}
            style={{
              flex: 1,
              background: '#1a1a2e',
            }}
          />

          {/* Chat panel */}
          {showChat && (
            <div style={{
              width: 320,
              borderLeft: '1px solid #e8e8e8',
              display: 'flex',
              flexDirection: 'column',
              background: '#fff',
            }}>
              {/* Chat header */}
              <div style={{
                padding: '12px 16px',
                borderBottom: '1px solid #e8e8e8',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <Text strong>Chat sa pacijentom</Text>
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={() => setShowChat(false)}
                />
              </div>

              {/* Messages */}
              <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: 16,
              }}>
                {chatMessages.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#999', padding: '40px 0' }}>
                    <MessageOutlined style={{ fontSize: 32, marginBottom: 8 }} />
                    <div>Nema poruka</div>
                    <div style={{ fontSize: 12 }}>Pošaljite poruku pacijentu</div>
                  </div>
                ) : (
                  chatMessages.map((msg) => (
                    <div
                      key={msg.id}
                      style={{
                        marginBottom: 12,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: msg.isLocal ? 'flex-end' : 'flex-start',
                      }}
                    >
                      <div style={{
                        fontSize: 11,
                        color: '#999',
                        marginBottom: 4,
                      }}>
                        {msg.isLocal ? 'Vi' : msg.sender} · {formatChatTime(msg.timestamp)}
                      </div>
                      <div style={{
                        padding: '8px 12px',
                        borderRadius: 12,
                        maxWidth: '85%',
                        background: msg.isLocal ? '#1890ff' : '#f0f0f0',
                        color: msg.isLocal ? '#fff' : '#333',
                      }}>
                        {msg.text}
                      </div>
                    </div>
                  ))
                )}
                <div ref={chatMessagesEndRef} />
              </div>

              {/* Chat input */}
              <div style={{
                padding: 12,
                borderTop: '1px solid #e8e8e8',
                display: 'flex',
                gap: 8,
              }}>
                <Input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onPressEnter={handleSendChatMessage}
                  placeholder="Napišite poruku..."
                  style={{ flex: 1 }}
                />
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleSendChatMessage}
                  disabled={!chatInput.trim()}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  // Pre-call screen - join the call when clicking button
  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '40px 20px' }}>
      <Link to={`/my-consultations/${id}`}>
        <Button icon={<ArrowLeftOutlined />} type="text" style={{ marginBottom: 24 }}>
          Nazad
        </Button>
      </Link>

      <Card>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <VideoCameraOutlined style={{ fontSize: 32, color: 'white' }} />
          </div>

          <Title level={3} style={{ marginBottom: 8 }}>{data.typeLabel}</Title>
          <Tag color="processing">{data.status === 'SCHEDULED' ? 'Zakazano' : 'Aktivno'}</Tag>
        </div>

        <Card size="small" style={{ marginBottom: 24 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space>
              <UserOutlined />
              <Text strong>Pacijent:</Text>
              <Text>{data.patient.name}</Text>
            </Space>
            <Space>
              <ClockCircleOutlined />
              <Text strong>Vreme:</Text>
              <Text>{formatTime(data.scheduledAt)} ({data.duration} min)</Text>
            </Space>
            {data.patient.phone && (
              <Space>
                <PhoneOutlined />
                <Text strong>Telefon:</Text>
                <a href={`tel:${data.patient.phone}`}>{data.patient.phone}</a>
              </Space>
            )}
          </Space>
        </Card>

        <Alert
          type="info"
          message="Pre ulaska u poziv"
          description="Proverite da vam radi kamera i mikrofon. Kao lekar, imate kontrolu nad cekaonom i mozete primati pacijente."
          style={{ marginBottom: 24 }}
        />

        <Button
          type="primary"
          size="large"
          block
          icon={<VideoCameraOutlined />}
          onClick={handleJoinCall}
          disabled={!joinToken}
          loading={!joinToken && !error}
          style={{
            height: 48,
            background: 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 100%)',
            border: 'none',
          }}
        >
          Zapocni video poziv
        </Button>
      </Card>
    </div>
  )
}
